import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FilterSetService } from '../../../services/filter-set.service';
import { BbTableService, TableRow } from '../../../services/bb-table.service';
import { Subscription } from 'rxjs';
import { PublicationService } from '../../../services/publication.service';
import { Publication } from 'app/interfaces/publication';
import { BbFilterConfiguration } from '../../shared/bb-filters/bb-filters.component';
import { TranslateService } from '@ngx-translate/core';
import { SharedPublicationIdToName } from '../../../pipes/sharedPublicationIdToName.pipe';
import { SapiService } from '../../../services/sapi.service';
import { AuthenticationService } from '../../../services/authentication.service';

@Component({
	selector: 'upload-transcode-publication-import',
	templateUrl: './upload-transcode-publication-import.component.html',
	styleUrls: ['./upload-transcode-publication-import.component.scss']
})

export class UploadTranscodePublicationImportComponent implements OnInit, OnDestroy {
	@Input() path: string;

	putRight = false;
	subscriptions: Subscription;
	rows: TableRow[] = [];
	sharingPublications: any = {};
	filterConfig: BbFilterConfiguration;

	search = true;
	presetToggable = true;
	lightBackground = true;

	private importing: string[] = [];
	private imported: string[] = [];

	constructor(
		private filterSetService: FilterSetService,
		private bbTableService: BbTableService,
		private publicationService: PublicationService,
		private translate: TranslateService,
		private sharedPublicationIdToName: SharedPublicationIdToName,
		private sapiService: SapiService,
		private authenticationService: AuthenticationService
	) {
		this.subscriptions = new Subscription();

		this.filterConfig = {
			search: true,
			filters: [
				{
					property: 'publicationid',
					label: this.translate.instant('PUBLICATION'),
					type: 'select',
					pipes: [this.sharedPublicationIdToName]
				},
				{
					property: 'mediatype',
					label: this.translate.instant('MEDIATYPE'),
					type: 'select'
				}, {
					property: 'sourcetype',
					label: this.translate.instant('SOURCETYPE'),
					type: 'select'
				}, {
					property: 'title',
					label: this.translate.instant('TITLE'),
					type: 'text'
				},
				{
					property: 'cat',
					label: this.translate.instant('TAGS'),
					type: 'select'
				}, {
					property: 'status',
					label: this.translate.instant('STATUS'),
					type: 'select'
				}, {
					property: 'publisheddate',
					label: this.translate.instant('PUBLISHED_DATE'),
					type: 'date'
				}, {
					property: 'createddate',
					label: this.translate.instant('CREATED_DATE'),
					type: 'date'
				}, {
					property: 'updateddate',
					label: this.translate.instant('UPDATED_DATE'),
					type: 'date'
				}, {
					property: 'sourceid',
					label: this.translate.instant('SOURCE_ID'),
					type: 'text'
				}, {
					property: 'isEmpty',
					label: this.translate.instant('IS_EMPTY'),
					type: 'boolean'
				}
			],
			presets: [
				{
					label: this.translate.instant('ALL'),
					value: 'all',
					filterSet: [],
				},
				{
					label: this.translate.instant('PUBLISHED'),
					value: 'published',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'status',
							operator: 'is',
							value: 'published'
						}]
					}],
				},
				{
					label: this.translate.instant('DRAFT'),
					value: 'draft',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'status',
							operator: 'is',
							value: 'draft'
						}]
					}],
				},
				{
					label: this.translate.instant('VIDEO'),
					value: 'video',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'mediatype',
							operator: 'is',
							value: 'video'
						}]
					}],
				},
				{
					label: this.translate.instant('AUDIO'),
					value: 'audio',
					filterSet: [{
						filters: [{
							type: 'mediaclip',
							field: 'mediatype',
							operator: 'is',
							value: 'audio'
						}]
					}],
				}
			]
		};

		this.filterSetService.setEntity('mediaclip');
		this.filterSetService.setSharedContext(true);

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
	}

	ngOnInit() {
		this.bbTableService
			.setConfigurable(false)
			.setEntity('mediaclip')
			.setLimit(15)
			.setFilterParam('context', 'shared')
			.linkToUrl();

		this.subscriptions.add(this.bbTableService.rowsRetrievedObservable.subscribe((rows: TableRow[]) => {
			if (rows) {
				this.rows = rows;
			}
		}));

		this.subscriptions.add(this.publicationService.getData().subscribe((data: Publication) => {
			if (data && data.sharingPublications && Array.isArray(data.sharingPublications)) {
				this.sharingPublications = {};
				this.sharingPublications[data.id] = data.name;
				for (const publication of data.sharingPublications) {
					if (publication.id && publication.name) {
						this.sharingPublications[publication.id] = publication.name;

						const preset = {
							label: publication.name,
							value: publication.id,
							filterSet: [{
								filters: [{
									type: 'mediaclip',
									field: 'publicationid',
									operator: 'is',
									value: publication.id
								}]
							}]
						};

						if (this.filterConfig.presets.indexOf(preset) === -1) {
							this.filterConfig.presets.push(preset);
						}
					}
				}
			}
		}));
	}

	importMediaclip(row: any) {
		const id = row.data.id;
		this.importing.push(id);

		this.sapiService.getUrl(`/mediaclip/${id}/import`).subscribe(
			(data) => {
				row.data.childid = data.id;
				this.importing.splice(this.importing.indexOf(id), 1);
				this.imported.push(id);
			},
			(err) => {
				this.importing.splice(this.importing.indexOf(id), 1);
				// console.log(err);
			}
		);
	}

	ngOnDestroy() {
		this.bbTableService.clearFilterParams();
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}
	}
}
